import { FontAwesome6 } from "@expo/vector-icons";
import { Text } from "@ui-kitten/components";
import Chevron from "components/Chevron";
import NoticeTag from "components/NoticeTag";
import dayjs from "dayjs";
import { EventAvailabilityDetails, EventNoticeDetails } from "features/types";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React from "react";
import { Image, StyleSheet, View } from "react-native";
import { hoursToDay } from "utils/dateUtils";
import { eventTypeDisplay, isSportsEvent } from "utils/eventUtils";
import { svgURL } from "utils/fileUtils";

interface ScheduleItemProps {
  event: EventAvailabilityDetails;
  shouldShowEventType: boolean;
  onPress?: () => void;
  showChevron?: boolean;
}

const ScheduleItem: React.FC<ScheduleItemProps> = ({
  event,
  shouldShowEventType,
  onPress,
  showChevron = true,
}) => {
  const {
    localDate,
    timezone,
    containsTime,
    opponentName,
    eventType,
    notices,
    imageUrl,
  } = event;
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const isSportsEventType = isSportsEvent(eventType);
  const eventImage = isSportsEventType
    ? svgURL(opponentName, false)
    : imageUrl!;

  const Notices = (props: { notices: EventNoticeDetails[] }) => (
    <>
      {props.notices
        .filter((notice) => notice.enabled)
        .filter(
          (notice) =>
            notice.displayTypes.includes("SCHEDULE") ||
            notice.displayTypes.includes("SCHEDULE_TOOLTIP"),
        )
        .map((notice) => {
          return (
            <View key={notice.id} className="mt-1">
              <NoticeTag
                type={notice.type}
                tag={notice.tag}
                shouldShowPopover
                description={notice.description}
              ></NoticeTag>
            </View>
          );
        })}
    </>
  );

  const EventDate = ({ event }: { event: EventAvailabilityDetails }) => {
    if (event.eventStatus !== "NORMAL") {
      return (
        <Text numberOfLines={1} ellipsizeMode="tail" category="h6">
          {event.eventStatus}
        </Text>
      );
    }
    return (
      <>
        <Text>{dayjs(localDate).tz(timezone).format("ddd")}</Text>
        <Text style={{ fontSize: 18, fontWeight: "bold" }}>
          {dayjs(localDate).tz(timezone).format("MMM DD")}
        </Text>
        {containsTime && (
          <Text numberOfLines={1}>
            {dayjs(localDate).tz(timezone).format("h:mm a")}
          </Text>
        )}
      </>
    );
  };

  const EventImage = () => {
    return (
      <View>
        <>
          {eventType !== "PARKING" && (
            <Image
              source={{
                uri: eventImage,
              }}
              style={{
                width: isSportsEvent(eventType) ? 50 : 75,
                height: 50,
                borderRadius: isSportsEvent(eventType) ? 0 : 8,
              }}
              resizeMode={isSportsEvent(eventType) ? "contain" : "cover"}
            />
          )}
          {eventType === "PARKING" && (
            <View className="w-[50px] h-[50px] justify-center items-center">
              <FontAwesome6 name="square-parking" size={50} color="#0271c0" />
            </View>
          )}
        </>
        <>
          {shouldShowEventType && (
            <Text category="c1" style={{ marginTop: 5 }}>
              {eventTypeDisplay(eventType)}
            </Text>
          )}
        </>
      </View>
    );
  };

  if (event.eventType === "EXPERIENCE") {
    return (
      <View className="mb-6">
        <View style={styles.container}>
          {containsTime && (
            <View style={{ flex: 0.3 }}>
              <EventDate event={event} />
            </View>
          )}
          <View style={{ flex: 0.5 }}>
            <Text style={{ fontSize: 18 }}>{event.eventName}</Text>
            {event.reservable ? (
              <Text style={{ fontSize: 12 }}>{event.venueName}</Text>
            ) : (
              <Text style={{ fontSize: 12 }}>
                Available in {hoursToDay(event.reservableInHours)}
              </Text>
            )}
          </View>

          <View style={{ flex: 0.3, alignItems: "center" }}>
            <EventImage />
          </View>
          <View>
            {event.reservable && showChevron ? (
              <Chevron />
            ) : (
              // works as a placeholder
              <View style={{ width: 36 }}></View>
            )}
          </View>
        </View>
        {event.minPlanTicketCharge && !tenantConfig?.hidePremiumTag && (
          <View className="mt-1">
            <NoticeTag type="PREMIUM" onPress={onPress} tag="Premium" />
          </View>
        )}
        <Notices notices={notices} />
      </View>
    );
  }

  return (
    <View className="mb-6">
      <View style={styles.container}>
        <View style={{ flex: 0.3 }}>
          <EventDate event={event} />
        </View>
        <View style={{ flex: 0.3 }}>
          <EventImage />
        </View>
        <View style={{ flex: 0.5 }}>
          <Text style={{ fontSize: 18 }}>{event.eventName}</Text>
          {event.reservable ? (
            <Text style={{ fontSize: 12 }}>{event.venueName}</Text>
          ) : (
            <Text style={{ fontSize: 12 }}>
              Available in {hoursToDay(event.reservableInHours)}
            </Text>
          )}
        </View>
        <View>
          {event.reservable && showChevron ? (
            <Chevron />
          ) : (
            // works as a placeholder
            <View style={{ width: 36 }}></View>
          )}
        </View>
      </View>
      {event.minPlanTicketCharge && !tenantConfig?.hidePremiumTag && (
        <View className="mt-1">
          <NoticeTag type="PREMIUM" onPress={onPress} tag="Premium" />
        </View>
      )}
      <Notices notices={notices} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
  },
});

export default ScheduleItem;
